import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Stack,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import LanguageIcon from '@mui/icons-material/Language';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { CircularProgress, Alert } from '@mui/material';
import { REPS_COLORS, oeBand } from '../../utils/constants';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUserOrganizations } from '../../hooks/useOrganization';
import { useSiteComparisons } from '../../hooks/useSiteComparisons';
import OrgSwitcher from '../../components/OrgSwitcher';

const QUADRANTS = ['reliability', 'efficiency', 'productivity', 'safety'];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const shortRegion = (name) => name.replace(/\s*Region$/i, '');

/**
 * Enterprise Rollup — Mindmap branch 2, "Enterprise Rollup"
 * LIVE: computed from the active organization's site snapshots.
 * Higher OE = worse; colors follow the REPS brand + green→red warning bands.
 */
function EnterpriseRollup() {
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  const organization = organizations[0] || null;
  const { comparisons, loading } = useSiteComparisons(organization?.id || null);

  const header = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}>
        <LanguageIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4" sx={{ flexGrow: 1 }}>Enterprise Rollup</Typography>
        <OrgSwitcher />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {comparisons ? (
          <>
            <strong>{comparisons.org.name}</strong> — {comparisons.siteScores.length} sites across{' '}
            {comparisons.regions.length} region{comparisons.regions.length !== 1 ? 's' : ''}
            {comparisons.org.industry ? ` (${comparisons.org.industry})` : ''}.{' '}
          </>
        ) : null}
        Higher scores mean more organizational entropy, so taller bars and warmer colors flag where to focus next.
      </Typography>
    </>
  );

  if (loading) {
    return <Box>{header}<Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box></Box>;
  }
  if (!comparisons || comparisons.siteScores.length === 0) {
    return (
      <Box>
        {header}
        <Alert severity="info">
          The enterprise rollup appears once your organization has site-level survey data.
        </Alert>
      </Box>
    );
  }

  const enterpriseAvg = comparisons.enterpriseAverage;
  const regions = comparisons.regions;

  // One row per region; one REPS-colored bar per quadrant.
  const regionChartData = regions.map((r) => {
    const a = comparisons.regionalAverages[r.id] || {};
    return {
      name: shortRegion(r.name),
      Reliability: a.reliability ?? 0,
      Efficiency: a.efficiency ?? 0,
      Productivity: a.productivity ?? 0,
      Safety: a.safety ?? 0,
    };
  });

  return (
    <Box>
      {header}

      <Grid container spacing={3}>
        {/* Enterprise averages */}
        {Object.entries(enterpriseAvg).map(([k, v]) => {
          const repsColor = REPS_COLORS[k] || '#666';
          const band = oeBand(v, organization?.thresholds, k);
          return (
            <Grid item xs={6} md={3} key={k}>
              <Card sx={{ height: '100%', borderTop: 4, borderTopColor: repsColor }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', color: repsColor, fontWeight: 700, lineHeight: 1.2 }}>{k}</Typography>
                  <Typography variant="h3" fontWeight={700} sx={{ color: band.color }}>{v.toFixed(1)}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Enterprise avg</Typography>
                    <Typography variant="caption" sx={{ color: band.color, fontWeight: 700 }}>{band.label}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={v * 10}
                    sx={{
                      height: 6, borderRadius: 3, mt: 0.5,
                      backgroundColor: 'rgba(0,0,0,0.08)',
                      '& .MuiLinearProgress-bar': { backgroundColor: band.color },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          );
        })}

        {/* Region comparison — grouped bars, REPS-colored */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>REPS by Region</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Each region&rsquo;s four quadrant averages. Taller = more entropy (worse).
              </Typography>
              <Box sx={{ height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={regionChartData} barCategoryGap="22%" barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                    <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Reliability" fill={REPS_COLORS.reliability} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Efficiency" fill={REPS_COLORS.efficiency} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Productivity" fill={REPS_COLORS.productivity} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Safety" fill={REPS_COLORS.safety} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Region summaries */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Regions</Typography>
              <Stack spacing={2} divider={<Divider />}>
                {regions.map((r) => {
                  const ravg = comparisons.regionalAverages[r.id];
                  const sitesInRegion = r.sites;
                  const improvingCount = sitesInRegion.filter((s) => s.trend === 'improving').length;
                  const worseningCount = sitesInRegion.filter((s) => s.trend === 'worsening').length;
                  // "Leaning toward" = the worst (highest entropy) quadrant.
                  const worst = ravg
                    ? QUADRANTS.reduce((a, b) => (ravg[b] > ravg[a] ? b : a), QUADRANTS[0])
                    : null;
                  return (
                    <Box key={r.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body1" fontWeight={700}>{r.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {/* Higher OE = worse: entropy easing is good (green), rising is bad (red) */}
                          {improvingCount > 0 && (
                            <Chip icon={<TrendingDownIcon />} label={`${improvingCount} improving`} size="small" color="success" variant="outlined" />
                          )}
                          {worseningCount > 0 && (
                            <Chip icon={<TrendingUpIcon />} label={`${worseningCount} worsening`} size="small" color="error" variant="outlined" />
                          )}
                        </Box>
                      </Box>

                      {worst && (
                        <Box sx={{ mt: 0.5, mb: 0.75 }}>
                          <Chip
                            label={`Leaning: ${cap(worst)} ${ravg[worst].toFixed(1)}`}
                            size="small"
                            sx={{ bgcolor: REPS_COLORS[worst], color: '#fff', fontWeight: 700 }}
                          />
                        </Box>
                      )}

                      {/* Per-quadrant values in REPS colors */}
                      {ravg && (
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                          {QUADRANTS.map((q) => (
                            <Box key={q} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ color: REPS_COLORS[q], fontWeight: 700 }}>{cap(q).charAt(0)}</Typography>
                              <Typography variant="body2" sx={{ color: oeBand(ravg[q], organization?.thresholds, q).color, fontWeight: 600 }}>{ravg[q].toFixed(1)}</Typography>
                            </Box>
                          ))}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
                            {sitesInRegion.length} site{sitesInRegion.length !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default EnterpriseRollup;
